import { OperationKeys, PrimaryKeyType } from '@decaf-ts/db-decorators';
import { Constructor } from '@decaf-ts/decoration';
import { formatDate, isValidDate, Model } from '@decaf-ts/decorator-validation';
import {
  CrudFieldComponent,
  DecafRepository,
  FieldsetComponent,
  ICrudFormEvent,
  KeyValue,
  NgxComponentDirective,
  TableComponent,
} from '@decaf-ts/for-angular';
import { CrudOperationKeys } from '@decaf-ts/ui-decorators';
import {
  Batch,
  convertDateToGS1Format,
  DatePattern,
  ManufacturerAddress,
  parseLegacyGS1DateToString,
  Product,
} from '@pharmaledgerassoc/ptp-toolkit/shared';
import { AppExpiryDateFieldComponent } from '../components/expiry-date/expiry-date-field.component';
import { BatchForm } from '../forms/BatchForm';
import { BatchLayout } from '../layouts/BatchLayout';
import { getNgxToastComponent } from '../utils/NgxToastComponent';
import { DatamatrixModalHandler } from './DatamatrixModalHandler';
import { EwBaseHandler } from './EwBaseHandler';

const HoursValues = {
  withDaySelection: '00:00:00',
  withoutDaySelection: '11:11:11',
};

const DatePatterns = {
  date: 'yyyy-MM-dd',
  month: 'yyyy-MM',
  formattedDate: 'dd/MM/yyyy',
  formatedMonth: 'MM/yyyy',
};

export function enableExpiryDateDaySelection(value: Date | string): boolean {
  if (typeof value === 'string') {
    const enableDaySelection = !value.includes('00');
    if (!value.includes('-')) {
      value = parseLegacyGS1DateToString(value);
    }
    value = parseExpiryDateValue(value, enableDaySelection) as Date;
  }
  const timeString = [value.getHours(), value.getMinutes(), value.getSeconds()]
    .map((n) => n.toString().padStart(2, '0'))
    .join(':');
  return timeString === HoursValues.withDaySelection;
}

export function formatExpiryDate(value: string | Date, enableDaySelection: boolean = true): string {
  if (typeof value === 'string') {
    value = parseExpiryDateValue(value, enableDaySelection) as Date;
  }
  return formatDate(value, enableDaySelection ? DatePatterns.formattedDate : DatePatterns.formatedMonth);
}

export function parseExpiryDateValue(
  value: string | Date,
  enableDaySelection: boolean = true,
  toString: boolean = false
): Date | string {
  if (isValidDate(value) && typeof value !== 'string') {
    value = formatDate(value, `${DatePattern} HH:mm:ss`);
  }
  value = (value as string).split(' ')[0];
  if (toString) {
    if (!enableDaySelection) {
      const dateArray = value.split('-');
      dateArray.pop();
      if (dateArray?.length) {
        value = dateArray.join('-');
      }
    }
    return value;
  }
  return new Date(`${value}T${enableDaySelection ? HoursValues.withDaySelection : HoursValues.withoutDaySelection}`);
}

export async function getExpiryDateDiffValue(value: string | Date, instance: NgxComponentDirective): Promise<string> {
  const enableDaySelection = enableExpiryDateDaySelection(value);
  const formatted = formatExpiryDate(value, enableDaySelection);
  return (await instance.translate('batch.diffs.expiryDate', [
    formatted,
    convertDateToGS1Format(formatted, '/'),
  ])) as string;
}

export async function getPreviewBarcodeFieldValue(instance: CrudFieldComponent) {
  if (!instance.value) {
    instance.value = `<span class="ti ti-qrcode"></span> ${await instance.translate('batch.dataMatrix.view')}`;
  }
  const element = instance.component?.nativeElement as HTMLIonItemElement;
  if (element) {
    element.classList.add('dcf-has-action');
    element.onclick = () => {
      DatamatrixModalHandler.showBarcodeModal(instance, BatchHandler._data as Batch);
    };
  }
}

export class BatchHandler extends EwBaseHandler {
  static uiOnlyProps = ['inventedName', 'nameMedicinalProduct', 'enableDaySelection', 'dataMatrix'] as string[];

  static products: Product[];

  static override skip = [
    'id',
    'owner',
    'updatedAt',
    'updatedBy',
    'version',
    'createdBy',
    'createdAt',
    ...BatchHandler.uiOnlyProps,
  ] as string[];

  static _data?: Batch;

  static query?: Product[];

  static operation?: OperationKeys;

  override async render(
    instance: TableComponent | AppExpiryDateFieldComponent | FieldsetComponent | CrudFieldComponent,
    prop: string,
    value: string
  ): Promise<string | void> {
    const operation = instance.operation;

    if (!BatchHandler.instance) {
      BatchHandler.instance = instance;
    }
    if ([OperationKeys.READ, OperationKeys.DELETE].includes(operation as OperationKeys)) {
      const model = instance._data as Batch;
      if (model) {
        BatchHandler._data = model;
      }
    } else {
      const model = instance.model as BatchLayout;
      if (model) {
        BatchHandler._data = model.batch as Batch;
      }
    }

    if (prop) {
      if (['inventedName', 'nameMedicinalProduct', 'batchNumber'].includes(prop)) {
        if (prop === 'batchNumber' && value) {
          return value;
        }
        value = await BatchHandler.getValueOnProductData(
          instance as TableComponent,
          prop as keyof Product,
          (BatchHandler._data as Batch) || (instance._data as Batch)
        );
        if (!this.operation) {
          return value;
        }
        if (!this.value) {
          this.value = value;
        }
      }
      if (prop === 'ManufacturerAddress' && instance.operation) {
        if ((instance._data as Batch)?.manufacturerAddress) {
          instance._data = (instance._data as Batch).manufacturerAddress;
          (instance as FieldsetComponent).refresh(instance.operation as CrudOperationKeys);
        }
      }
      if (['enableDaySelection'].includes(prop) && instance._data && operation === OperationKeys.UPDATE) {
        const { expiryDate } = instance._data as Batch;
        const component = instance as AppExpiryDateFieldComponent;
        const enableDaySelection = enableExpiryDateDaySelection(expiryDate);
        const date = parseExpiryDateValue(expiryDate, enableDaySelection) as Date;
        component.enableDaySelection = enableDaySelection;
        component.calendarInputValue = formatDate(
          date,
          component.enableDaySelection ? DatePatterns.date : DatePatterns.month
        );
        component.expiryDate = convertDateToGS1Format(`${parseExpiryDateValue(date, enableDaySelection, true)}`, '-');
      }
    }
  }

  override async handle<C extends NgxComponentDirective>(
    event: ICrudFormEvent,
    data: BatchLayout,
    instance?: C
  ): Promise<void> {
    const { role } = event;
    let result = false;
    let submited = false;
    let errorMessage;
    let err;
    // to avoid model being undefined when navigation back is called
    if (instance) {
      if (!instance.model) {
        const model = Model.get('BatchLayout') as Constructor<Model>;
        if (model) {
          instance.model = new model();
        }
      }
    }
    BatchHandler.loadingMessage = await this.translate(BatchHandler.loadingMessage, { '0': '' });
    switch (role) {
      case OperationKeys.CREATE:
      case OperationKeys.UPDATE: {
        BatchHandler.instance = instance;
        await BatchHandler.showLoading();
        const batch = (event.data as BatchLayout).batch as BatchForm;
        const { address } = batch;
        if (address) {
          delete batch.address;
          const addressArray = Object.values(address).flatMap((item) => (Array.isArray(item) ? item : [item]));
          event.data = {
            batch: {
              ...batch,
              manufacturerAddress: addressArray,
            },
          };
        }
        const { success, aborted, message, error } = await this.submit(event, false);
        submited = !aborted;
        result = success;
        errorMessage = message;
        err = error;
        break;
      }
      case OperationKeys.DELETE: {
        const { context, data } = event;
        if (!context) {
          const { success, aborted, message, error } = await this.submit({ ...event, data }, false);
          submited = !aborted;
          result = success;
          errorMessage = message;
          err = error;
        }
      }
    }

    if (submited) {
      const toast = getNgxToastComponent();
      if (result) {
        await EwBaseHandler.handleRedirect(instance as NgxComponentDirective, `batches`);
        await toast.show({
          color: 'dark',
          message: await this.translate(`operations.multiple.success`),
        });
      } else {
        await toast.show({
          color: 'danger',
          message: await this.translate(errorMessage, {
            '0': BatchHandler.pk,
            '1': data.batch.batchNumber,
          }),
        });
        await BatchHandler.handleError(instance as NgxComponentDirective, `batches`, err);
      }
      if (BatchHandler.loading.isVisible()) {
        await BatchHandler.loading.remove();
      }
      BatchHandler.data = undefined;
    }
  }

  override async beforeCreate<M extends Model>(model: M): Promise<M> {
    return BatchHandler.parseBatchData(model);
  }

  override async beforeUpdate<M extends Model>(
    data: M,
    repository: DecafRepository<M>,
    modelId: PrimaryKeyType
  ): Promise<boolean | M> {
    return BatchHandler.showModalDiffs(BatchHandler.parseBatchData(data), repository, modelId);
  }

  static parseBatchData<M extends Model>(model: M): M {
    const { expiryDate, enableDaySelection } = model as M & BatchForm;
    const date = parseExpiryDateValue(expiryDate, enableDaySelection);
    const result = Model.build({ ...model, expiryDate: date }, Batch.name) as KeyValue;
    for (const prop of BatchHandler.uiOnlyProps) {
      if (prop === 'address') {
        const eventData = BatchHandler.model as BatchForm;
        if (eventData) {
          const value = eventData[prop];
          const entries = Object.entries(value as ManufacturerAddress) || undefined;
          const address: ManufacturerAddress[] = [];
          if (entries.length) {
            for (const [, v] of entries) {
              if (v.address !== '') {
                const item: ManufacturerAddress = Model.build(v, ManufacturerAddress.name);
                address.push(item);
              }
            }
            result['manufacturerAddress'] = address;
          }
        }
        delete result[prop];
      }
      if (prop in model) {
        delete result[prop];
      }
    }
    result['manufacturerAddress'] = result['manufacturerAddress'] ?? [];
    return result as M;
  }

  static async getValueOnProductData(instance: TableComponent, prop: string, model: Batch) {
    if (!instance._query?.length) {
      if (!BatchHandler.query?.length) {
        BatchHandler.query = (await this.queryModelData(Product.name)) as Product[];
      }
      instance._query = BatchHandler.query;
    }
    if (!instance.model) {
      instance.model = model;
    }
    BatchHandler._data = instance.model as Batch;
    const query = instance._query as Product[];
    if (prop === 'batchNumber') {
      return (instance.model as Batch)?.batchNumber || '';
    }
    if (query && instance.model) {
      const product = query.find((p) => p?.productCode === (instance.model as Batch).productCode) as Product;
      if (product && product?.[prop as keyof Product]) {
        return product[prop as keyof typeof product] as string;
      }
    }
    return '';
  }

  static async parseExpiryFieldValue(instance: NgxComponentDirective): Promise<void> {
    if (instance._data && instance.operation) {
      const { expiryDate } = instance._data as Batch;
      const enableDaySelection = enableExpiryDateDaySelection(expiryDate);
      if (
        [OperationKeys.READ, OperationKeys.DELETE].includes(instance.operation as OperationKeys) &&
        !String(instance.value).includes('GS1')
      ) {
        if (instance.value) {
          const value = formatDate(
            new Date(instance.value as string),
            enableDaySelection ? DatePatterns.formattedDate : DatePatterns.formatedMonth
          );
          instance.value = await instance.translate('batch.expiryDate.gs1Format', [
            value,
            convertDateToGS1Format(instance.value as string),
          ]);
        }
      } else {
        const component = instance as AppExpiryDateFieldComponent;
        const date = parseExpiryDateValue(expiryDate, enableDaySelection) as Date;
        component.enableDaySelection = enableDaySelection;
        component.calendarInputValue = formatDate(
          date,
          component.enableDaySelection ? DatePatterns.date : DatePatterns.month
        );
        component.expiryDate = convertDateToGS1Format(`${parseExpiryDateValue(date, enableDaySelection, true)}`, '-');
      }
    }
  }
}
